import { useEffect, useMemo, useState } from 'react';
import TravelPlanner from './TravelPlanner';
import logo from './AppiHolidays Branding.png';
import AppV2 from './v2/AppV2';

const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'it', 'ja', 'pt-BR', 'pt-PT', 'pt'];

const HEADER_TRANSLATIONS = {
  en: {
    subtitle: 'Smart itineraries with room booking',
    shareButton: 'Share on WhatsApp',
    shareText: 'Check out AppiHolidays',
    desktopMode: 'Desktop planner mode',
    languageLabel: 'Language',
  },
  es: {
    subtitle: 'Itinerarios inteligentes con reserva de habitaciones',
    shareButton: 'Compartir en WhatsApp',
    shareText: 'Mira AppiHolidays',
    desktopMode: 'Modo planificador de escritorio',
    languageLabel: 'Idioma',
  },
  fr: {
    subtitle: 'Itineraires intelligents avec reservation de chambre',
    shareButton: 'Partager sur WhatsApp',
    shareText: 'Decouvrez AppiHolidays',
    desktopMode: 'Mode planificateur de bureau',
    languageLabel: 'Langue',
  },
  de: {
    subtitle: 'Intelligente Reiserouten mit Zimmerbuchung',
    shareButton: 'Auf WhatsApp teilen',
    shareText: 'Schau dir AppiHolidays an',
    desktopMode: 'Desktop-Planermodus',
    languageLabel: 'Sprache',
  },
  it: {
    subtitle: 'Itinerari intelligenti con prenotazione camere',
    shareButton: 'Condividi su WhatsApp',
    shareText: 'Scopri AppiHolidays',
    desktopMode: 'Modalita planner desktop',
    languageLabel: 'Lingua',
  },
  ja: {
    subtitle: '宿泊予約付きのスマート旅行プラン',
    shareButton: 'WhatsAppで共有',
    shareText: 'AppiHolidaysをチェックしてみてください',
    desktopMode: 'デスクトップ向けプランナーモード',
    languageLabel: '言語',
  },
  'pt-BR': {
    subtitle: 'Roteiros inteligentes com reserva de quartos',
    shareButton: 'Compartilhar no WhatsApp',
    shareText: 'Confira o AppiHolidays',
    desktopMode: 'Modo planejador desktop',
    languageLabel: 'Idioma',
  },
  'pt-PT': {
    subtitle: 'Roteiros inteligentes com reserva de quartos',
    shareButton: 'Partilhar no WhatsApp',
    shareText: 'Conheca o AppiHolidays',
    desktopMode: 'Modo de planeador desktop',
    languageLabel: 'Idioma',
  },
  pt: {
    subtitle: 'Roteiros inteligentes com reserva de quartos',
    shareButton: 'Partilhar no WhatsApp',
    shareText: 'Conheca o AppiHolidays',
    desktopMode: 'Modo de planeador desktop',
    languageLabel: 'Idioma',
  },
};

const detectPreferredLanguage = () => {
  if (typeof navigator === 'undefined') return 'en';

  const preferred = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  for (const item of preferred) {
    const locale = String(item || '').trim();
    if (!locale) continue;

    const exactMatch = SUPPORTED_LOCALES.find((supported) => supported.toLowerCase() === locale.toLowerCase());
    if (exactMatch) {
      return exactMatch;
    }

    const shortCode = locale.toLowerCase().split('-')[0];
    if (SUPPORTED_LOCALES.includes(shortCode)) {
      return shortCode;
    }
  }

  return 'en';
};

function App() {
  const [language, setLanguage] = useState(() => detectPreferredLanguage());
  const params = new URLSearchParams(window.location.search);
  const path = String(window.location.pathname || '/').toLowerCase();
  const isV2Path = path === '/v2' || path.startsWith('/v2/');
  const forceV2 = (params.get('version') || '').toLowerCase() === '2' || params.get('v2') === '1';
  const modeParam = (params.get('mode') || 'live').toLowerCase();
  const v2Mode = modeParam === 'demo' ? 'demo' : 'live';
  const t = useMemo(() => {
    if (HEADER_TRANSLATIONS[language]) return HEADER_TRANSLATIONS[language];
    const base = String(language || '').toLowerCase().split('-')[0];
    return HEADER_TRANSLATIONS[base] || HEADER_TRANSLATIONS.en;
  }, [language]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const shareAppOnWhatsApp = async () => {
    if (typeof window === 'undefined') return;

    const shareUrl = 'https://www.appiholidays.com/';
    const shareText = `${t.shareText}: ${shareUrl}`;
    const encodedText = encodeURIComponent(shareText);

    try {
      window.location.href = `whatsapp://send?text=${encodedText}`;
      setTimeout(() => {
        window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
      }, 500);
    } catch {
      window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
    }
  };

  if (isV2Path || forceV2) return <AppV2 mode={v2Mode} />;

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full border-b border-emerald-100 bg-white/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/80 lg:sticky lg:top-0 lg:z-40">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center lg:justify-between px-4 py-1 lg:px-8 lg:py-4">
          {/* Logo block — image prominent, text below on all sizes */}
          <div className="flex flex-col items-center gap-0 w-full lg:w-auto lg:flex-row lg:items-center lg:gap-4">
            <img
              src={logo}
              alt="AppiHolidays Logo"
              className="h-56 w-56 sm:h-64 sm:w-64 lg:h-36 lg:w-36 object-contain drop-shadow-sm"
            />
            <div className="mt-1 text-center w-full lg:mt-0 lg:w-auto lg:text-left">
              <h1 className="text-base font-extrabold tracking-tight text-slate-900 lg:text-lg">AppiHolidays</h1>
              <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-emerald-700 lg:text-[11px]">
                {t.subtitle}
              </div>
              <button
                onClick={shareAppOnWhatsApp}
                type="button"
                className="mt-2 inline-flex items-center justify-center rounded-full border border-green-600 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
              >
                {t.shareButton}
              </button>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3 text-sm text-slate-600">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">{t.desktopMode}</span>
            <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs">
              <span className="font-semibold text-slate-700">{t.languageLabel}</span>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="bg-transparent text-slate-700 outline-none"
                aria-label="Language"
              >
                <option value="en">English</option>
                <option value="es">Espanol</option>
                <option value="fr">Francais</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="ja">日本語</option>
                <option value="pt-BR">Portugues (Brasil)</option>
                <option value="pt-PT">Portugues (Portugal)</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <TravelPlanner language={language} />
    </div>
  );
}

export default App;

